import {useCallback, useContext, useSyncExternalStore} from 'react';
import {selectIsAiRedesignInProgress} from '@Components/email-maker-ai-redesign-prompt-box/email-maker-ai-redesign-prompt-box-reducer';
import {useAppSelector} from '@/hooks';
import {selectIsColorPaletteBeingApplied} from '@/reducers/color-palettes.reducer';
import {emailMakerContext} from '@EmailMakerReact/email-maker-react.context';
import {EmailMakerMode} from '@EmailMakerReact/email-maker-react.types';
import {getCurrentIdEmailTemplate, getCurrentMode} from '@EmailMakerReact/lib/email-maker-storage-library';

const STORAGE_AFTER_STORE_EVENT = 'storage:after:store';

/**
 * Whether version history panel can be accessed or not
 */
export function useIsVersionHistoryAccessible(): boolean {
  const {editor, store} = useContext(emailMakerContext);
  const isAiRedesignInProgress = useAppSelector(selectIsAiRedesignInProgress);
  const isColorPaletteBeingApplied = useAppSelector(selectIsColorPaletteBeingApplied);

  const subscribe = useCallback(
    (onStoreChange: () => void): (() => void) => {
      if (!editor) {
        return (): void => undefined;
      }
      editor.on(STORAGE_AFTER_STORE_EVENT, onStoreChange);
      return (): void => {
        editor.off(STORAGE_AFTER_STORE_EVENT, onStoreChange);
      };
    },
    [editor]
  );

  const getSnapshot = useCallback((): boolean => {
    return editor !== null && getCurrentMode(editor) === EmailMakerMode.LOAD && getCurrentIdEmailTemplate(editor) !== null;
  }, [editor]);

  const isEditingSavedTemplate = useSyncExternalStore(subscribe, getSnapshot);

  if (editor === null || store.isEmbedded) {
    return false;
  }

  if (isAiRedesignInProgress || isColorPaletteBeingApplied) {
    return false;
  }

  return isEditingSavedTemplate;
}
